import React, { useEffect, useMemo, useState } from 'react';
import { Avatar } from '@roger/ui-components';
import { cliniciansToSelectOptions } from '../../utils/clinicianUtils';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';
import {
  Activity,
  Briefcase,
  CalendarClock,
  Check,
  Loader2,
  MapPin,
  Sparkles,
  Users,
} from 'lucide-react';

type MatchTier = 'top' | 'strong' | 'okay';

type ReasonItem = {
  icon: React.ReactNode;
  text: string;
};

type CaseloadLevel = 'Low' | 'Medium' | 'High';

type Suggestion = {
  id: string;
  name: string;
  credentials?: string;
  initials: string;
  score: number;
  tier: MatchTier;
  reasons: ReasonItem[];
};

type Option = { value: string; label: string };

interface SmartSchedulingSuggestionsProps {
  episodeStartDate: string | null | undefined;
  /** Raw roster; clinician and case-manager picks are derived from this. */
  clinicians: AvailableClinicianResponse[];
  selectedClinicianId?: string | null;
  selectedCaseManagerId?: string | null;
  loadingRoster: boolean;
  onSelectClinician: (id: string) => void;
  onSelectCaseManager: (id: string) => void;
}

// Stable djb2-like hash so generated mock data is deterministic per id.
const hashString = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return Math.abs(h);
};

const pickFrom = <T,>(arr: T[], seed: number): T => arr[seed % arr.length];

const parseLabel = (
  label: string
): { name: string; credentials?: string; initials: string } => {
  const [nameRaw, credsRaw] = label.split(',').map(s => s.trim());
  const parts = nameRaw.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  const initials = (first + last).toUpperCase() || '?';
  return { name: nameRaw, credentials: credsRaw || undefined, initials };
};

const formatDateLong = (iso?: string | null): string => {
  if (!iso) return '';
  const raw = iso.split('T')[0];
  const [y, m, d] = raw.split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const tierForScore = (score: number): MatchTier => {
  if (score >= 9) return 'top';
  if (score >= 7) return 'strong';
  return 'okay';
};

const TIER_CLASSES: Record<MatchTier, string> = {
  top: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  strong: 'bg-sky-50 text-sky-700 border-sky-200',
  okay: 'bg-amber-50 text-amber-700 border-amber-200',
};

const CASELOAD_BY_TIER: Record<MatchTier, CaseloadLevel> = {
  top: 'Low',
  strong: 'Medium',
  okay: 'High',
};

const CITY_POOL = [
  'Van Nuys',
  'Pasadena',
  'Burbank',
  'Glendale',
  'Torrance',
  'Encino',
];

const SPECIALTY_POOL = [
  'wound care',
  'CHF management',
  'post-op recovery',
  'diabetes education',
  'COPD management',
];

/**
 * Deterministically derive mocked recommendation metadata for a given roster list.
 * We sort by a hash of the id so the "top picks" are stable across renders, and
 * we vary the score tier by position (top → strong → okay).
 */
const buildSuggestions = (
  options: Option[],
  role: 'clinician' | 'case_manager'
): Suggestion[] => {
  if (options.length === 0) return [];

  const ranked = options
    .slice()
    .sort((a, b) => hashString(a.value + role) - hashString(b.value + role))
    .slice(0, Math.min(3, options.length));

  // One score per ranked slot. Keeps the ordering visually sensible (top slot
  // has the highest score) while still varying slightly per user via the seed.
  const scoreBands: Array<[number, number]> = [
    [9, 10],
    [7, 8],
    [5, 6],
  ];

  return ranked.map((opt, i) => {
    const seed = hashString(opt.value + role);
    const band = scoreBands[i] ?? scoreBands[scoreBands.length - 1];
    const score = band[0] + (seed % (band[1] - band[0] + 1));
    const tier = tierForScore(score);
    const caseload = CASELOAD_BY_TIER[tier];
    const { name, credentials, initials } = parseLabel(opt.label);

    const distance = (1 + (seed % 90) / 10).toFixed(1);
    const nearbyPatients = 2 + (seed % 6);
    const daysFree = 1 + (seed % 4);

    let reasons: ReasonItem[];
    if (role === 'clinician') {
      reasons = [
        {
          icon: <MapPin className='h-3.5 w-3.5 text-gray-500' />,
          text: `${distance} mi from patient in ${pickFrom(CITY_POOL, seed)}`,
        },
        {
          icon: <Users className='h-3.5 w-3.5 text-gray-500' />,
          text: `Serves ${nearbyPatients} active patient${nearbyPatients === 1 ? '' : 's'} within 5 mi`,
        },
        {
          icon: <Activity className='h-3.5 w-3.5 text-gray-500' />,
          text: `${caseload} caseload — can accept a new SOC`,
        },
      ];
      if (seed % 3 === 0) {
        reasons.push({
          icon: <Briefcase className='h-3.5 w-3.5 text-gray-500' />,
          text: `Strong ${pickFrom(SPECIALTY_POOL, seed)} history`,
        });
      }
    } else {
      reasons = [
        {
          icon: <Activity className='h-3.5 w-3.5 text-gray-500' />,
          text: `${caseload} caseload — can accept a new patient`,
        },
        {
          icon: <CalendarClock className='h-3.5 w-3.5 text-gray-500' />,
          text: `Next OASIS review slot in ${daysFree} day${daysFree === 1 ? '' : 's'}`,
        },
        {
          icon: <Users className='h-3.5 w-3.5 text-gray-500' />,
          text: `Coordinating ${nearbyPatients} other patients on this team`,
        },
      ];
    }

    return { id: opt.value, name, credentials, initials, score, tier, reasons };
  });
};

const ScoreChip: React.FC<{ score: number; tier: MatchTier }> = ({
  score,
  tier,
}) => (
  <span
    className={`inline-flex items-baseline gap-0.5 rounded-md border px-1.5 py-0.5 text-xs font-semibold ${TIER_CLASSES[tier]}`}
    aria-label={`Match score ${score} out of 10`}
  >
    <span className='text-sm leading-none'>{score}</span>
    <span className='text-[10px] font-medium opacity-80'>/10</span>
  </span>
);

const LoadingSkeletons: React.FC<{ count: number }> = ({ count }) => (
  <div className='flex flex-col gap-2'>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className='rounded-lg border border-gray-200 bg-white px-3 py-2.5'
      >
        <div className='flex gap-3 animate-pulse'>
          <div className='h-7 w-7 rounded-full bg-gray-200' />
          <div className='flex-1 space-y-2'>
            <div className='h-3.5 w-2/3 rounded bg-gray-200' />
            <div className='h-3 w-3/4 rounded bg-gray-100' />
            <div className='h-3 w-1/2 rounded bg-gray-100' />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SuggestionCard: React.FC<{
  suggestion: Suggestion;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: () => void;
}> = ({ suggestion, isSelected, isLoading, onSelect }) => {
  const base =
    'group relative w-full text-left rounded-lg border bg-white px-3 py-2.5 transition-all';
  const interactive = isLoading
    ? 'cursor-wait opacity-70'
    : 'hover:border-indigo-300 hover:shadow-sm';
  const state = isSelected
    ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-sm'
    : 'border-gray-200';

  return (
    <button
      type='button'
      onClick={onSelect}
      disabled={isLoading}
      className={`${base} ${interactive} ${state}`}
      aria-pressed={isSelected}
    >
      <div className='flex items-start gap-3'>
        <Avatar size='sm' initials={suggestion.initials} />
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm font-medium text-gray-900'>
              {suggestion.name}
              {suggestion.credentials && (
                <span className='text-xs font-normal text-gray-500'>
                  , {suggestion.credentials}
                </span>
              )}
            </span>
            <ScoreChip score={suggestion.score} tier={suggestion.tier} />
          </div>
          <ul className='mt-1.5 flex flex-col gap-1'>
            {suggestion.reasons.map((r, i) => (
              <li
                key={i}
                className='flex items-start gap-1.5 text-xs text-gray-600'
              >
                <span className='mt-0.5 shrink-0'>{r.icon}</span>
                <span>{r.text}</span>
              </li>
            ))}
          </ul>
        </div>
        {isSelected && (
          <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700'>
            <Check className='h-3 w-3' />
            Selected
          </span>
        )}
      </div>
    </button>
  );
};

const SuggestionColumn: React.FC<{
  title: string;
  suggestions: Suggestion[];
  selectedId?: string | null;
  isLoading: boolean;
  emptyLabel: string;
  onSelect: (id: string) => void;
}> = ({ title, suggestions, selectedId, isLoading, emptyLabel, onSelect }) => {
  const showSkeletons = isLoading && suggestions.length === 0;
  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <h3 className='text-xs font-semibold uppercase tracking-wide text-gray-600'>
          {title}
        </h3>
        {suggestions.length > 0 && (
          <span className='text-[11px] text-gray-400'>
            Top {suggestions.length} match{suggestions.length === 1 ? '' : 'es'}
          </span>
        )}
      </div>

      {showSkeletons ? (
        <LoadingSkeletons count={3} />
      ) : suggestions.length === 0 ? (
        <div className='rounded-md border border-dashed border-gray-200 bg-white/60 px-3 py-4 text-xs text-gray-500'>
          {emptyLabel}
        </div>
      ) : (
        <div
          className={`flex flex-col gap-2 transition-opacity duration-200 ${isLoading ? 'opacity-60' : 'opacity-100'}`}
        >
          {suggestions.map(s => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              isSelected={selectedId === s.id}
              isLoading={isLoading}
              onSelect={() => onSelect(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const SmartSchedulingSuggestions: React.FC<
  SmartSchedulingSuggestionsProps
> = ({
  episodeStartDate,
  clinicians,
  selectedClinicianId,
  selectedCaseManagerId,
  loadingRoster,
  onSelectClinician,
  onSelectCaseManager,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(true);

  // Derive the per-role option lists from the single raw roster source.
  const clinicianOptions = useMemo(
    () => cliniciansToSelectOptions(clinicians, 'clinician'),
    [clinicians]
  );
  const caseManagerOptions = useMemo(
    () => cliniciansToSelectOptions(clinicians, 'caseManager'),
    [clinicians]
  );

  // Simulate re-running the recommendation model whenever the SOC date changes.
  // For the prototype we return the same mocked suggestions, but the loading
  // indicator conveys that Roger re-evaluated the plan. We keep this effect
  // idempotent (no pre-effect mutation + early-return) so React Strict Mode's
  // double-invoke in dev doesn't leave us stuck in the loading state.
  useEffect(() => {
    setIsRefreshing(true);
    const t = setTimeout(() => setIsRefreshing(false), 1500);
    return () => clearTimeout(t);
  }, [episodeStartDate]);

  const clinicianSuggestions = useMemo(
    () => buildSuggestions(clinicianOptions, 'clinician'),
    [clinicianOptions]
  );
  const caseManagerSuggestions = useMemo(
    () => buildSuggestions(caseManagerOptions, 'case_manager'),
    [caseManagerOptions]
  );

  const isLoading = loadingRoster || isRefreshing;
  const formattedDate = formatDateLong(episodeStartDate);

  return (
    <section className='mb-4 rounded-lg border border-indigo-100 bg-gradient-to-br from-indigo-50/70 via-white to-white'>
      <div className='flex items-center justify-between gap-3 border-b border-indigo-100 px-4 py-3'>
        <div className='flex items-center gap-2'>
          <div className='inline-flex h-7 w-7 items-center justify-center rounded-md bg-indigo-600/10 text-indigo-700'>
            <Sparkles className='h-4 w-4' />
          </div>
          <div>
            <div className='text-sm font-semibold text-gray-900'>
              Smart scheduling suggestions
            </div>
            <div className='text-xs text-gray-500'>
              Roger's recommendations
              {formattedDate ? ` for SOC on ${formattedDate}` : ' for this SOC'}
              . Click a card to assign, or use the dropdowns below to override.
            </div>
          </div>
        </div>
        {isLoading && (
          <div className='inline-flex shrink-0 items-center gap-1.5 rounded-full bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700'>
            <Loader2 className='h-3 w-3 animate-spin' />
            Updating…
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 gap-4 p-4 lg:grid-cols-2'>
        <SuggestionColumn
          title='Suggested clinician'
          suggestions={clinicianSuggestions}
          selectedId={selectedClinicianId}
          isLoading={isLoading}
          emptyLabel='No clinicians available for this clinic yet.'
          onSelect={onSelectClinician}
        />
        <SuggestionColumn
          title='Suggested case manager'
          suggestions={caseManagerSuggestions}
          selectedId={selectedCaseManagerId}
          isLoading={isLoading}
          emptyLabel='No case managers available for this clinic yet.'
          onSelect={onSelectCaseManager}
        />
      </div>
    </section>
  );
};

export default SmartSchedulingSuggestions;
